import styled from "styled-components";
import { getFontStyles } from "@/redux/cmsConfig/utils";

const COMPACT_CARD_HEIGHT = "24rem";

const Details = styled.div<{ $compact?: boolean }>(({ $compact }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: $compact ? 0 : undefined,
}));

const Description = styled.div<{ $compact?: boolean }>(
  ({ theme, $compact }) => ({
    ...getFontStyles(theme.menu_product_card.font),
    margin: ".25rem 0 0",
    position: "relative",

    ...($compact
      ? {
          display: "block",
          overflow: "hidden",
          maxHeight: "4.8em",
          paddingBottom: "1.25rem",
          paddingRight: "3rem",
        }
      : {
          display: "-webkit-box",
          overflow: "hidden",
          "-webkit-box-orient": "vertical",
          "-webkit-line-clamp": 3,
        }),

    "&::before": $compact
      ? {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "1.75rem",
          pointerEvents: "none",
          background: `linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0),
            ${theme.menu_product_card.background_color ?? "#ffffff"}
          )`,
        }
      : {},

    "&::after": $compact
      ? {
          content: '"(...)"',
          position: "absolute",
          bottom: 0,
          right: 0,
          pointerEvents: "none",
          zIndex: 1,
          paddingLeft: ".5rem",
          backgroundColor:
            theme.menu_product_card.background_color ?? "#ffffff",
          color: theme.menu_product_card.font.color ?? "currentColor",
          fontWeight: 700,
          lineHeight: 1,
        }
      : {},

    p: {
      margin: 0,
    },
  })
);

const Footer = styled.footer({
  margin: "auto 0 0",
  padding: "1.5rem 0 0",
});

const Image = styled.div<{ $compact?: boolean }>(({ theme, $compact }) => ({
  aspectRatio: "1 / 1",
  borderRadius: theme.global.border_radius * 0.5 ?? 0,
  maxWidth: "100%",
  overflow: "hidden",
  position: "relative",
  backgroundColor: $compact
    ? theme.menu_product_card.background_color ?? "transparent"
    : "transparent",

  img: {
    objectFit: $compact ? "contain" : "cover",
    objectPosition: "center",
    display: "block",
  },
}));

const Title = styled.h2<{ $compact?: boolean }>(({ theme, $compact }) => ({
  ...getFontStyles(theme.menu_product_card.secondary_font, {
    line_height: (value) => {
      if (value === "revert") {
        return "145%";
      }

      return value;
    },
  }),
  margin: 0,
  ...($compact
    ? {
        flex: "0 0 auto",
        minHeight: "1.5em",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      }
    : {}),
}));

const Horizontal = styled.article<{ $compact?: boolean; hasImage: boolean }>(
  ({ theme, hasImage = false, $compact }) => ({
    alignItems: "center",
    border: "1px solid",
    borderColor: theme.menu_product_card.border_color ?? "transparent",
    borderRadius: theme.menu_product_card.border_radius ?? 0,
    display: "grid",
    height: $compact ? COMPACT_CARD_HEIGHT : "auto",
    overflow: $compact ? "hidden" : "visible",
    gridTemplateColumns: hasImage ? "1fr 5.625rem" : "1fr",
    gap: "1rem",
    padding: ".875rem 1rem",
    ...getFontStyles(theme.menu_product_card.font, {
      line_height: (value) => {
        if (value === "revert") {
          return "145%";
        }

        return value;
      },
    }),

    "@media (min-width: 1024px)": {
      gridTemplateColumns: hasImage ? "1fr 9.25rem" : "1fr",
    },

    [`& ${Image}`]: {
      gridColumn: "2",
    },

    [`& ${Details}`]: {
      gridColumn: "1",
      gridRow: "1",
    },
  })
);

const Vertical = styled.article<{ $compact?: boolean }>(
  ({ theme, $compact }) => ({
    border: "1px solid",
    borderColor: theme.menu_product_card.border_color ?? "transparent",
    borderRadius: theme.menu_product_card.border_radius ?? 0,
    display: "flex",
    flexDirection: "column",
    height: $compact ? COMPACT_CARD_HEIGHT : "auto",
    overflow: $compact ? "hidden" : "visible",
    padding: ".75rem",
    ...getFontStyles(theme.menu_product_card.font, {
      line_height: (value) => {
        if (value === "revert") {
          return "145%";
        }

        return value;
      },
    }),

    "@media (min-width: 768px)": {
      padding: ".875rem 1rem",
    },

    [`& ${Image} + ${Details}`]: {
      marginTop: "1rem",
    },
  })
);

export const Card = {
  Details,
  Description,
  Footer,
  Horizontal,
  Image,
  Title,
  Vertical,
};
